/**
 * Altdaten-Bruecke: localStorage -> Server
 *
 * Seit der Umstellung auf das Express-Backend ist der Server die Quelle der
 * Wahrheit. Diese Datei schreibt selbst KEINE Nutzdaten mehr in den Browser —
 * sie liest nur noch die Altlasten aus der localStorage-Zeit aus und raeumt
 * sie auf Wunsch weg. Die frueheren Helfer (loadLeads/saveLeads/
 * loadSettings/saveSettings, saveFile/getFile) sind entfallen: sie hatten
 * keine Aufrufer mehr und haetten Geschaeftsdaten weiterhin im Browser
 * abgelegt, wo sie niemand sonst sieht.
 *
 * Einzige Schreibvorgaenge hier: die beiden Migrations-Marker
 * (MIGRATION_DONE_KEY, IMPORTED_KEY) — reine Buchfuehrung ueber die
 * Uebernahme, keine Geschaeftsdaten.
 */

// ==========================================
// ALTDATEN (Migration Browser -> Server)
// ==========================================

/**
 * Schluessel, die von der Datenuebernahme angefasst (gelesen und geloescht)
 * werden duerfen. Alles, was hier NICHT steht, kann ueber clearLegacyData()
 * nicht geloescht werden.
 */
export const LEGACY_KEYS = {
  leads: 'aiqcon_leads',
  kalkulationen: 'aiqcon_kalkulationen',
  sharedKalkulationen: 'aiqcon_shared_kalkulationen',
  suppliers: 'aiqcon_suppliers',
  supplierPrices: 'aiqcon_supplier_prices',
  offers: 'aiqcon_offers',
  offerEvents: 'aiqcon_offer_events',
  marginSettings: 'aiqcon_margin_settings',
  strukturaufschlag: 'aiqcon_strukturaufschlag',
  settings: 'aiqcon_settings',
};

/**
 * Schluessel, die bewusst NICHT zur Altlast gehoeren und niemals ueber die
 * Datenuebernahme geloescht werden duerfen. Dient als Dokumentation UND als
 * zweite Sperre in clearLegacyData(), falls jemand spaeter unbedacht einen
 * dieser Schluessel in LEGACY_KEYS eintraegt.
 *
 *  - aiqcon_token           Anmeldung. Loeschen wuerde den Nutzer abmelden.
 *  - aiqcon_leads_view      UI-Voreinstellung (Kanban/Liste), keine Nutzdaten.
 *  - aiqcon_futures_cache   reiner Anzeige-Cache, baut sich selbst neu auf.
 *  - aiqcon_migration_done  der Fertig-Marker selbst.
 */
export const PROTECTED_KEYS = Object.freeze([
  'aiqcon_token',
  'aiqcon_leads_view',
  'aiqcon_futures_cache',
  'aiqcon_migration_done',
]);

export const MIGRATION_DONE_KEY = 'aiqcon_migration_done';

// Buchfuehrung ueber bereits uebernommene Alt-Datensaetze. Noetig, wenn eine
// Uebernahme nur teilweise geklappt hat: dann bleibt der Fertig-Marker aus,
// die schon angelegten Datensaetze duerfen aber nicht ein zweites Mal
// hochgeladen werden.
const IMPORTED_KEY = 'aiqcon_migration_imported';

/**
 * Erwartete Form je Schluessel. Listen-Schluessel, die etwas anderes als ein
 * Array enthalten, sind kaputt; Einstellungs-Schluessel duerfen dagegen ein
 * Objekt oder ein einzelner Wert sein — die duerfen nicht faelschlich als
 * "unlesbar" gemeldet werden.
 */
const LEGACY_SHAPES = {
  [LEGACY_KEYS.leads]: 'array',
  [LEGACY_KEYS.kalkulationen]: 'array',
  [LEGACY_KEYS.sharedKalkulationen]: 'array',
  [LEGACY_KEYS.suppliers]: 'array',
  [LEGACY_KEYS.supplierPrices]: 'array',
  [LEGACY_KEYS.offers]: 'array',
  [LEGACY_KEYS.offerEvents]: 'array',
  [LEGACY_KEYS.marginSettings]: 'value',
  [LEGACY_KEYS.strukturaufschlag]: 'value',
  [LEGACY_KEYS.settings]: 'value',
};

const ABSENT = { present: false, items: [], count: 0, corrupt: false };

/**
 * Einen Alt-Schluessel lesen, ohne etwas zu raten.
 * @param {string} key
 * @param {'array'|'value'} expected
 * @returns {{ present: boolean, items: Array, count: number, corrupt: boolean }}
 */
const readLegacyEntry = (key, expected) => {
  let raw;
  try {
    raw = localStorage.getItem(key);
  } catch {
    // Kein Zugriff auf localStorage (z.B. blockierte Cookies): dann gibt es
    // hier auch nichts aufzuraeumen.
    return { ...ABSENT };
  }

  if (raw === null) return { ...ABSENT };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Der Schluessel existiert, der Inhalt ist Schrott. Nicht uebernehmbar,
    // aber loeschbar — das muss der Nutzer erfahren.
    return { present: true, items: [], count: 0, corrupt: true };
  }

  if (Array.isArray(parsed)) {
    return { present: true, items: parsed, count: parsed.length, corrupt: false };
  }

  if (expected === 'array') {
    // Liste erwartet, etwas anderes vorgefunden.
    return { present: true, items: [], count: 0, corrupt: true };
  }

  // Einzelwert/Objekt (Einstellungen): zaehlt als ein Eintrag, null als keiner.
  return { present: true, items: [], count: parsed === null ? 0 : 1, corrupt: false };
};

/**
 * Liest alle bekannten Alt-Schluessel aus dem localStorage.
 * Es wird nichts geraten und nichts ergaenzt — was nicht im Browser liegt,
 * kommt auch nicht zurueck.
 *
 * @returns {{
 *   leads: Array, kalkulationen: Array, suppliers: Array, supplierPrices: Array,
 *   counts: Record<string, number>,
 *   countsByKey: Record<string, number>,
 *   presentKeys: string[], corruptKeys: string[], total: number, hasLegacyData: boolean
 * }}
 *   counts ist nach logischem Namen indiziert (counts.leads),
 *   countsByKey nach dem localStorage-Schluessel (countsByKey['aiqcon_leads']).
 */
export const readLegacyData = () => {
  const entries = {};
  Object.entries(LEGACY_KEYS).forEach(([name, key]) => {
    entries[name] = { key, ...readLegacyEntry(key, LEGACY_SHAPES[key] || 'array') };
  });

  const list = Object.values(entries);
  const presentKeys = list.filter((e) => e.present).map((e) => e.key);
  const corruptKeys = list.filter((e) => e.corrupt).map((e) => e.key);

  const counts = {};
  const countsByKey = {};
  Object.entries(entries).forEach(([name, e]) => {
    counts[name] = e.count;
    countsByKey[e.key] = e.count;
  });

  const total = list.reduce((sum, e) => sum + e.count, 0);

  return {
    // Nur Leads werden uebernommen — nur die brauchen die Rohdaten.
    leads: entries.leads.items,
    kalkulationen: entries.kalkulationen.items,
    suppliers: entries.suppliers.items,
    supplierPrices: entries.supplierPrices.items,
    counts,
    countsByKey,
    presentKeys,
    corruptKeys,
    total,
    // Auch ein leerer, aber vorhandener Schluessel gilt als Altlast — er darf
    // aufgeraeumt werden, auch wenn es nichts zu uebernehmen gibt.
    hasLegacyData: presentKeys.length > 0,
  };
};

/**
 * Entfernt Alt-Schluessel aus dem localStorage.
 * @param {string[]} [keys] - zu entfernende Schluessel; ohne Angabe alle aus LEGACY_KEYS.
 *                            Unbekannte und geschuetzte Schluessel werden ignoriert.
 * @returns {{ removed: string[], skipped: string[] }}
 */
export const clearLegacyData = (keys) => {
  const allowed = Object.values(LEGACY_KEYS).filter((key) => !PROTECTED_KEYS.includes(key));
  const wanted = Array.isArray(keys) && keys.length > 0 ? keys : allowed;
  const removed = [];
  const skipped = [];

  wanted.forEach((key) => {
    if (!allowed.includes(key)) {
      skipped.push(key);
      return;
    }
    try {
      localStorage.removeItem(key);
      removed.push(key);
    } catch {
      skipped.push(key);
    }
  });

  return { removed, skipped };
};

/**
 * Marker: wurde die Uebernahme in diesem Browser schon durchgefuehrt?
 * @returns {{ done: boolean, at: string|null, count: number|null }}
 */
export const getMigrationStatus = () => {
  try {
    const raw = localStorage.getItem(MIGRATION_DONE_KEY);
    if (!raw) return { done: false, at: null, count: null };
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return { done: true, at: parsed.at || null, count: Number.isFinite(parsed.count) ? parsed.count : null };
      }
    } catch {
      // Aeltere Variante: nur ein Zeitstempel als String.
    }
    return { done: true, at: String(raw), count: null };
  } catch {
    return { done: false, at: null, count: null };
  }
};

export const isMigrationDone = () => getMigrationStatus().done;

/**
 * Setzt den Marker, damit niemand versehentlich ein zweites Mal uebernimmt.
 * @param {number} count - Anzahl der uebernommenen Datensaetze
 */
export const markMigrationDone = (count = 0) => {
  try {
    localStorage.setItem(
      MIGRATION_DONE_KEY,
      JSON.stringify({ at: new Date().toISOString(), count: Number(count) || 0 })
    );
    return true;
  } catch {
    return false;
  }
};

/**
 * Marker zuruecksetzen (nur fuer Support-/Testfaelle).
 */
export const resetMigrationMarker = () => {
  try {
    localStorage.removeItem(MIGRATION_DONE_KEY);
    localStorage.removeItem(IMPORTED_KEY);
    return true;
  } catch {
    return false;
  }
};

/** @returns {string[]} Fingerabdruecke bereits uebernommener Datensaetze */
export const getImportedLegacyIds = () => {
  try {
    const raw = localStorage.getItem(IMPORTED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((v) => String(v)) : [];
  } catch {
    return [];
  }
};

/**
 * Fingerabdruecke als uebernommen vermerken (Duplikate werden zusammengefasst).
 * @param {string[]} ids
 */
export const addImportedLegacyIds = (ids) => {
  if (!Array.isArray(ids) || ids.length === 0) return true;
  try {
    const merged = new Set(getImportedLegacyIds());
    ids.forEach((id) => merged.add(String(id)));
    localStorage.setItem(IMPORTED_KEY, JSON.stringify([...merged]));
    return true;
  } catch {
    return false;
  }
};
